import React from 'react';
import { useHistory } from 'react-router-dom';
import ErrorBoundary, { ErrorFallbackProps } from './ErrorBoundary';

interface RouteErrorBoundaryProps {
  children: React.ReactNode;
  fallbackRoute?: string;
}

const RouteErrorFallback: React.FC<ErrorFallbackProps & { fallbackRoute?: string }> = ({ 
  error, 
  resetError, 
  errorId,
  fallbackRoute = '/' 
}) => {
  const history = useHistory();

  const handleGoHome = () => {
    history.push(fallbackRoute);
    resetError();
  };

  const handleGoBack = () => {
    history.goBack();
    resetError();
  };

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
    padding: '2rem',
    textAlign: 'center',
    backgroundColor: 'var(--color-background)',
    color: '#ffffff'
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: '1rem',
    color: 'var(--color-accent)'
  };

  const messageStyle: React.CSSProperties = {
    fontSize: '1.1rem',
    marginBottom: '2rem',
    lineHeight: 1.6,
    maxWidth: '600px'
  };

  const buttonContainerStyle: React.CSSProperties = {
    display: 'flex',
    gap: '1rem',
    flexWrap: 'wrap',
    justifyContent: 'center'
  };

  const buttonStyle: React.CSSProperties = {
    padding: '0.75rem 1.5rem',
    backgroundColor: 'var(--color-accent)',
    color: '#ffffff',
    border: 'none',
    borderRadius: 'var(--border-radius-sm)',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '600',
    transition: 'var(--transition-fast)',
    minWidth: '120px'
  };

  const secondaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: 'transparent',
    border: '1px solid var(--color-accent)'
  };

  return (
    <div style={containerStyle}>
      <h1 style={titleStyle}>🚨 Page Error</h1>
      
      <p style={messageStyle}>
        We encountered an error while loading this page. This might be a temporary issue.
        You can try going back to the previous page or return to the home page.
      </p>

      <div style={buttonContainerStyle}>
        <button 
          style={buttonStyle}
          onClick={handleGoHome}
          onMouseOver={(e) => e.currentTarget.style.opacity = '0.8'}
          onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
        >
          Go to Home
        </button>
        
        <button 
          style={secondaryButtonStyle}
          onClick={handleGoBack}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-accent)';
            e.currentTarget.style.borderColor = 'var(--color-accent)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.borderColor = 'var(--color-accent)';
          }}
        >
          Go Back
        </button>
        
        <button 
          style={secondaryButtonStyle}
          onClick={resetError}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-accent)';
            e.currentTarget.style.borderColor = 'var(--color-accent)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.borderColor = 'var(--color-accent)';
          }}
        >
          Try Again
        </button>
      </div>

      {process.env.NODE_ENV === 'development' && (
        <details style={{ marginTop: '2rem', textAlign: 'left' }}>
          <summary style={{ cursor: 'pointer', marginBottom: '1rem' }}>
            Error Details (Development)
          </summary>
          <pre style={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.1)', 
            padding: '1rem', 
            borderRadius: 'var(--border-radius-sm)',
            overflow: 'auto',
            fontSize: '0.875rem'
          }}>
            {error?.message}
            {error?.stack && `\n\nStack:\n${error.stack}`}
            {`\n\nError ID: ${errorId}`}
          </pre>
        </details>
      )}
    </div>
  );
};

const RouteErrorBoundary: React.FC<RouteErrorBoundaryProps> = ({
  children,
  fallbackRoute = '/'
}) => {
  return (
    <ErrorBoundary
      fallbackComponent={(props) => (
        <RouteErrorFallback {...props} fallbackRoute={fallbackRoute} />
      )}
      resetOnPropsChange={true}
    >
      {children}
    </ErrorBoundary>
  );
};

export default RouteErrorBoundary;